'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactPathwaysProps {
  onPathwaySelect: (pathway: 'discovery' | 'general') => void;
  selectedPathway: 'discovery' | 'general';
}

interface PathwayOption {
  id: 'discovery' | 'general';
  icon: string;
  title: string;
  description: string;
  benefits: string[];
}

const ContactPathways = ({ onPathwaySelect, selectedPathway }: ContactPathwaysProps) => {
  const pathways: PathwayOption[] = [
    {
      id: 'discovery',
      icon: 'RocketLaunchIcon',
      title: 'Book Discovery Call',
      description: 'Enterprise leaders seeking AI transformation support',
      benefits: [
        '30-minute consultation',
        'AI readiness assessment',
        'Custom transformation roadmap',
        'No-obligation discussion'
      ]
    },
    {
      id: 'general',
      icon: 'EnvelopeIcon',
      title: 'General Enquiry',
      description: 'Questions, partnerships, or other opportunities',
      benefits: [
        'Flexible communication',
        'Quick response time',
        'Multiple contact options',
        'Personalized support'
      ]
    }
  ];

  return (
    <section className="py-12 lg:py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold font-headline text-primary mb-4">
              Choose Your Engagement Path
            </h2>
            <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
              Select the option that best matches your needs. We'll guide you through the appropriate next steps.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
            {pathways.map((pathway) => (
              <button
                key={pathway.id}
                onClick={() => onPathwaySelect(pathway.id)}
                className={`text-left p-6 lg:p-8 rounded-lg border-2 transition-all duration-300 hover:shadow-card ${
                  selectedPathway === pathway.id
                    ? 'border-secondary bg-secondary/5 shadow-card'
                    : 'border-border bg-card hover:border-secondary/50'
                }`}
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-6 ${
                  selectedPathway === pathway.id
                    ? 'bg-secondary text-white' :'bg-primary/10 text-primary'
                }`}>
                  <Icon name={pathway.icon as any} size={28} variant="outline" />
                </div>
                <h3 className="text-xl font-bold font-headline text-primary mb-3">
                  {pathway.title}
                </h3>
                <p className="text-sm text-muted-foreground font-body mb-6">
                  {pathway.description}
                </p>
                <ul className="space-y-3">
                  {pathway.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Icon 
                        name="CheckCircleIcon" 
                        size={20} 
                        variant="solid"
                        className={selectedPathway === pathway.id ? 'text-secondary' : 'text-primary/60'}
                      />
                      <span className="text-sm text-foreground font-body">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactPathways;